import React, { useEffect, useState } from "react";
import { Box, Collapse, useTheme } from "@mui/material";
import { ViewWeek } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { ScheduleToolbarProps } from "../../Schedule/partial/ScheduleToolbar.partial";
import {
  dashboard2ScheduleChromeMinimalSx,
  dashboard2ScheduleChromeRowSx,
  dashboard2SectionFieldsetLegendIconSx,
  DASHBOARD2_SCHEDULE_FILTER_PANEL_ID
} from "../dashboard2-status.styles";
import { Dashboard2ScheduleFilterPanel } from "./Dashboard2ScheduleFilterPanel";
import { Dashboard2ScheduleFilterTrigger } from "./Dashboard2ScheduleFilterTrigger";
import { Dashboard2ScheduleManageColleaguesControl } from "./Dashboard2ScheduleManageColleaguesControl";
import {
  Dashboard2ScheduleLayout,
  Dashboard2ScheduleLayoutToggleGroup
} from "./Dashboard2ScheduleLayoutToggleGroup";
import { Dashboard2SectionPanel } from "./Dashboard2SectionPanel";

export { Dashboard2ScheduleLayout } from "./Dashboard2ScheduleLayoutToggleGroup";

export type Dashboard2ScheduleToolbarProps = Omit<ScheduleToolbarProps, "viewToggleSlot">;

const isFilterOpenForLayout = (layout: Dashboard2ScheduleLayout): boolean =>
  layout !== Dashboard2ScheduleLayout.Week;

type Dashboard2ScheduleControlsProps = {
  layout: Dashboard2ScheduleLayout;
  onLayoutChange: (layout: Dashboard2ScheduleLayout) => void;
  toolbarProps: Dashboard2ScheduleToolbarProps;
  filterOpenSignal?: number;
};

export const Dashboard2ScheduleControls: React.FC<Dashboard2ScheduleControlsProps> = ({
  layout,
  onLayoutChange,
  toolbarProps,
  filterOpenSignal = 0
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [filterExpanded, setFilterExpanded] = useState(() => isFilterOpenForLayout(layout));

  useEffect(() => {
    setFilterExpanded(isFilterOpenForLayout(layout));
  }, [layout]);

  useEffect(() => {
    if (filterOpenSignal > 0) {
      setFilterExpanded(true);
    }
  }, [filterOpenSignal]);

  const selectedColleagueCount = toolbarProps.isColleagueMode
    ? toolbarProps.selectedColleagues.length
    : undefined;

  return (
    <Dashboard2SectionPanel
      icon={<ViewWeek fontSize="small" />}
      title={t("_dashboard2_scheduleLayoutGroup")}
      description={t("_dashboard2_scheduleControlsDescription")}
      iconSx={dashboard2SectionFieldsetLegendIconSx(theme)}
      panelSx={dashboard2ScheduleChromeMinimalSx(theme)}
      data-testid="dashboard2-schedule-controls"
      headerTestId="dashboard2-schedule-controls-header"
    >
      <Box sx={dashboard2ScheduleChromeRowSx}>
        <Dashboard2ScheduleLayoutToggleGroup
          layout={layout}
          onLayoutChange={onLayoutChange}
          compact
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Dashboard2ScheduleManageColleaguesControl />

          <Dashboard2ScheduleFilterTrigger
            expanded={filterExpanded}
            onToggle={() => setFilterExpanded(previous => !previous)}
            selectedColleagueCount={selectedColleagueCount}
            filterPanelId={DASHBOARD2_SCHEDULE_FILTER_PANEL_ID}
          />
        </Box>
      </Box>

      <Collapse in={filterExpanded} unmountOnExit id={DASHBOARD2_SCHEDULE_FILTER_PANEL_ID}>
        <Dashboard2ScheduleFilterPanel {...toolbarProps} />
      </Collapse>
    </Dashboard2SectionPanel>
  );
};
